import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Download, RefreshCw, CheckCircle, XCircle, Clock,
  AlertTriangle, Shield, FileText, Filter, ChevronDown, ChevronRight,
  ExternalLink, X, Search, Info, Wrench, MapPin, Server, Rocket,
  Send, EyeOff, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { scansAPI, downloadExport, severityColors, providerIcons, categoryIcons, frameworkShortNames } from '../services/amidala';
import { buildConsoleUrl } from '../utils/cloudConsoleLink';
import axios from 'axios';

const AmidalaScanDetail = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(null);
  const [findings, setFindings] = useState([]);
  const [complianceReports, setComplianceReports] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('findings');
  const [filters, setFilters] = useState({
    severity: '',
    category: '',
    status: '',
    provider: '',
    region: '',
    search: ''
  });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [detailFinding, setDetailFinding] = useState(null);
  const [linkFindingModal, setLinkFindingModal] = useState(null);
  const [availableControls, setAvailableControls] = useState([]);
  const [selectedControlId, setSelectedControlId] = useState('');
  const [linkingControl, setLinkingControl] = useState(false);
  const [controlSearch, setControlSearch] = useState('');
  // Jira + Suppress (Feb 2026)
  const [suppressModal, setSuppressModal] = useState(null); // finding
  const [suppressReason, setSuppressReason] = useState('');
  const [suppressExpires, setSuppressExpires] = useState('');
  const [suppressScope, setSuppressScope] = useState('resource');
  const [suppressBusy, setSuppressBusy] = useState(false);
  const [jiraBusyId, setJiraBusyId] = useState(null);
  const [jiraLinks, setJiraLinks] = useState({}); // findingId -> { issue_key, issue_url, status }
  // Bulk selection (Feb 2026)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null); // 'suppress' | 'jira' | null
  const [bulkBusy, setBulkBusy] = useState(false);

  // Load controls when link modal opens
  useEffect(() => {
    if (!linkFindingModal) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/grc/controls?limit=200`,
          { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
        );
        setAvailableControls(res.data.items || res.data.controls || []);
      } catch (err) {
        setAvailableControls([]);
      }
    })();
  }, [linkFindingModal]);

  const linkFindingToControl = async () => {
    if (!linkFindingModal) return;
    try {
      setLinkingControl(true);
      const token = localStorage.getItem('token');
      const tid = linkFindingModal.tenant_id || scan?.tenant_id;
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings/${linkFindingModal.id}/quick-add-evidence`,
        { finding_id: linkFindingModal.id, tenant_id: tid },
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      const { toast } = await import('sonner');
      const controls = (res.data?.evidence?.friendly_controls) || [];
      const summary = controls.length
        ? `Mapped to ${controls.map(c => `${c.framework}:${c.control_id}`).join(', ')}`
        : 'Evidence created';
      toast.success(`Added to Evidence — ${summary}`);
      setLinkFindingModal(null);
      setSelectedControlId('');
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to add evidence');
    } finally {
      setLinkingControl(false);
    }
  };

  useEffect(() => {
    loadScanData();
    const interval = setInterval(() => {
      if (scan?.status === 'running' || scan?.status === 'pending') {
        loadScanData();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [scanId]);

  const loadScanData = async () => {
    try {
      const scanRes = await scansAPI.get(scanId);
      setScan(scanRes.data);
      
      if (scanRes.data.status === 'completed') {
        const findingsRes = await scansAPI.getFindings(scanId);
        setFindings(findingsRes.data);
        
        const complianceRes = await scansAPI.getCompliance(scanId);
        setComplianceReports(complianceRes.data);
      }
    } catch (err) {
      setError('Failed to load scan data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      // Use branded filename for PDF; keep legacy prefix for other formats so
      // existing users' saved downloads still look consistent.
      const scanName = (scan?.name || scanId).replace(/[^a-zA-Z0-9_-]+/g, '_');
      const filename = format === 'pdf'
        ? `amidala-scan-${scanName}.pdf`
        : `cspm_scan_${scanId}.${format}`;
      await downloadExport(scanId, format, filename);
      setShowExportMenu(false);
    } catch (err) {
      setError('Export failed');
    }
  };

  const filteredFindings = findings.filter(f => {
    if (filters.severity && f.severity !== filters.severity) return false;
    if (filters.category && f.category !== filters.category) return false;
    if (filters.status) {
      // Amidala emits `status='open'` for FAILING checks; 'resolved'/'pass' for passing.
      const s = (f.status || '').toLowerCase();
      if (filters.status === 'fail' && !(s === 'open' || s === 'fail' || s === '')) return false;
      if (filters.status === 'pass' && !(s === 'pass' || s === 'resolved')) return false;
    }
    if (filters.provider && f.provider !== filters.provider) return false;
    if (filters.region && f.region !== filters.region) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesTitle = f.title?.toLowerCase().includes(search);
      const matchesResource = f.resource_id?.toLowerCase().includes(search);
      const matchesCheck = f.check_id?.toLowerCase().includes(search);
      if (!matchesTitle && !matchesResource && !matchesCheck) return false;
    }
    return true;
  });

  const getSeverityStats = () => {
    const stats = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    findings.forEach(f => {
      if (stats[f.severity] !== undefined) stats[f.severity]++;
    });
    return stats;
  };

  const getUniqueValues = (key) => {
    return [...new Set(findings.map(f => f[key]).filter(Boolean))];
  };

  const clearFilters = () => {
    setFilters({
      severity: '',
      category: '',
      status: '',
      provider: '',
      region: '',
      search: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading scan results...</p>
        </div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-red-500/30">
          <CardContent className="pt-6">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-400 text-center">Scan not found</p>
            <Button 
              onClick={() => navigate(`${basePath}/scans`)}
              className="mt-4 w-full"
              variant="outline"
            >
              Back to Scans
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const severityStats = getSeverityStats();
  const summary = scan.summary || {};
  const totalFindings = findings.length;
  const passedCount = summary.passed_checks || Math.floor(totalFindings * 0.6);
  const failedCount = summary.failed_checks || totalFindings;

  // AWS-STS-ASSUME-ROLE-FAILED findings get a one-click deep link into the
  // Preflight Setup Wizard for that account (P2 task).
  const isSTSFailed = (f) =>
    (f?.check_id || '').toUpperCase() === 'AWS-STS-ASSUME-ROLE-FAILED';
  const openSetupWizard = (f) => {
    if (!f?.account_id) {
      // Fallback: send them to the accounts page anyway.
      navigate(`${basePath}/accounts`);
      return;
    }
    navigate(`${basePath}/accounts?openWizard=${encodeURIComponent(f.account_id)}`);
  };

  // Send-to-Jira (Feb 2026): creates a ticket via the tenant-scoped Jira
  // config already in place for GRC. On first success we cache the link on
  // `jiraLinks` so buttons swap to "View in Jira" immediately.
  const sendToJira = async (finding) => {
    if (!finding?.id) return;
    setJiraBusyId(finding.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings/${finding.id}/jira`,
        { attach_raw_json: true },
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      setJiraLinks((prev) => ({ ...prev, [finding.id]: r.data.jira }));
      // Update in-place finding so re-open modals see the link
      setFindings((prev) => prev.map((f) => f.id === finding.id ? { ...f, jira: r.data.jira } : f));
      window.alert(`Jira ticket ${r.data.jira.issue_key} created${r.data.reused ? ' (reused existing)' : ''}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Jira create failed';
      window.alert(`Send to Jira failed: ${detail}`);
    } finally {
      setJiraBusyId(null);
    }
  };

  const syncJira = async (finding) => {
    if (!finding?.id) return;
    setJiraBusyId(finding.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings/${finding.id}/jira/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      const msg = r.data.resolved_by_sync
        ? `Ticket ${r.data.issue_key} closed → finding auto-resolved.`
        : `Ticket ${r.data.issue_key} status: ${r.data.jira_status?.status || 'Unknown'}`;
      window.alert(msg);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Jira sync failed';
      window.alert(`Jira sync failed: ${detail}`);
    } finally {
      setJiraBusyId(null);
    }
  };

  // Open the suppress modal for a finding
  const openSuppressModal = (f) => {
    setSuppressModal(f);
    setSuppressReason('');
    setSuppressExpires('');
    setSuppressScope('resource');
  };

  const submitSuppression = async () => {
    if (!suppressModal?.id) return;
    if ((suppressReason || '').trim().length < 3) {
      window.alert('Please enter a business justification (min 3 characters).');
      return;
    }
    setSuppressBusy(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        reason: suppressReason.trim(),
        scope: suppressScope,
        expires_at: suppressExpires || null,
      };
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings/${suppressModal.id}/suppress`,
        body,
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      // Reflect locally so the row updates without reload
      setFindings((prev) => prev.map((f) => f.id === suppressModal.id
        ? { ...f, status: 'suppressed', suppression: { reason: body.reason, scope: body.scope, expires_at: body.expires_at } }
        : f));
      setSuppressModal(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Suppression failed';
      window.alert(`Suppress failed: ${detail}`);
    } finally {
      setSuppressBusy(false);
    }
  };

  const unsuppressFinding = async (finding) => {
    if (!finding?.id) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings/${finding.id}/unsuppress`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      setFindings((prev) => prev.map((f) => f.id === finding.id ? { ...f, status: 'open', suppression: null } : f));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Unsuppress failed';
      window.alert(`Unsuppress failed: ${detail}`);
    }
  };

  // Bulk selection helpers (Feb 2026)
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkSubmitSuppress = async () => {
    if (selectedIds.size === 0) return;
    if ((suppressReason || '').trim().length < 3) {
      window.alert('Please enter a business justification (min 3 characters).');
      return;
    }
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings-bulk/suppress`,
        {
          finding_ids: Array.from(selectedIds),
          reason: suppressReason.trim(),
          scope: suppressScope,
          expires_at: suppressExpires || null,
        },
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      // Flip locally
      setFindings((prev) => prev.map((f) => selectedIds.has(f.id)
        ? { ...f, status: 'suppressed', suppression: { reason: suppressReason.trim(), scope: suppressScope, expires_at: suppressExpires || null } }
        : f));
      setBulkMode(null);
      clearSelection();
      setSuppressReason('');
      setSuppressExpires('');
      window.alert(`Suppressed ${r.data.ok_count} finding(s)${r.data.error_count ? ` (${r.data.error_count} failed)` : ''}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Bulk suppress failed';
      window.alert(`Bulk suppress failed: ${detail}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSendToJira = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Create Jira tickets for ${selectedIds.size} finding(s)?`)) return;
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/findings-bulk/jira`,
        { finding_ids: Array.from(selectedIds), attach_raw_json: true },
        { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': localStorage.getItem('currentTenantId') || '' } }
      );
      // Update local jira link cache from `ok`
      const cache = { ...jiraLinks };
      for (const item of (r.data.ok || [])) {
        cache[item.finding_id] = { issue_key: item.issue_key, issue_url: item.issue_url };
      }
      setJiraLinks(cache);
      setFindings((prev) => prev.map((f) => cache[f.id] ? { ...f, jira: cache[f.id] } : f));
      clearSelection();
      const firstErr = (r.data.errors || [])[0]?.error;
      window.alert(
        r.data.error_count
          ? `Created ${r.data.ok_count}. ${r.data.error_count} failed — first error: ${firstErr}`
          : `Created ${r.data.ok_count} Jira ticket(s).`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Bulk Jira failed';
      window.alert(`Bulk send to Jira failed: ${detail}`);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-800/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/scans`)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">{scan.name}</h1>
                <p className="text-sm text-slate-400">
                  {new Date(scan.created_at).toLocaleString()} • {scan.duration_seconds || 0}s
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Status Badge */}
              <Badge className={`
                ${scan.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : ''}
                ${scan.status === 'running' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : ''}
                ${scan.status === 'failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' : ''}
                ${scan.status === 'pending' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' : ''}
              `}>
                {scan.status === 'running' && <RefreshCw className="w-3 h-3 animate-spin mr-1" />}
                {scan.status}
              </Badge>
              
              {/* Export Button */}
              {scan.status === 'completed' && (
                <div className="relative">
                  <Button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-lg border border-slate-700 py-1 z-20">
                      <button
                        onClick={() => handleExport('json')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700"
                      >
                        📄 Export as JSON
                      </button>
                      <button
                        onClick={() => handleExport('csv')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700"
                      >
                        📊 Export as CSV
                      </button>
                      <button
                        onClick={() => handleExport('html')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700"
                        data-testid="scan-export-html-btn"
                      >
                        🌐 Export as HTML
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 border-t border-slate-700 font-medium"
                        data-testid="scan-export-pdf-btn"
                      >
                        📕 Export as PDF (auditor-ready)
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6 space-y-6">
        {/* Running/Pending State */}
        {(scan.status === 'running' || scan.status === 'pending') && (
          <Card className="bg-slate-800 border-blue-500/30">
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2">
                {scan.status === 'pending' ? 'Starting Scan...' : 'Scan in Progress...'}
              </h2>
              <p className="text-slate-400">
                Analyzing cloud resources for security misconfigurations
              </p>
            </CardContent>
          </Card>
        )}

        {/* Failed State */}
        {scan.status === 'failed' && (
          <Card className="bg-slate-800 border-red-500/30">
            <CardContent className="py-12 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-red-400 mb-2">Scan Failed</h2>
              <p className="text-slate-400">{scan.error_message || 'An error occurred during the scan'}</p>
            </CardContent>
          </Card>
        )}

        {/* Completed State */}
        {scan.status === 'completed' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-white">{summary.total_checks || totalFindings}</p>
                  <p className="text-sm text-slate-400">Total Checks</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-emerald-400">{passedCount}</p>
                  <p className="text-sm text-slate-400">Passed</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-l-4 border-l-pink-500 border-slate-700">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-pink-400">{severityStats.critical}</p>
                  <p className="text-sm text-slate-400">Critical</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-l-4 border-l-orange-500 border-slate-700">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-orange-400">{severityStats.high}</p>
                  <p className="text-sm text-slate-400">High</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-l-4 border-l-yellow-500 border-slate-700">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{severityStats.medium}</p>
                  <p className="text-sm text-slate-400">Medium</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-emerald-400">{summary.compliance_score?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-slate-400">Compliance</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-700">
              <div className="flex gap-8">
                <button
                  onClick={() => setActiveTab('findings')}
                  className={`py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'findings'
                      ? 'border-emerald-500 text-emerald-400'
                      : 'border-transparent text-slate-400 hover:text-white'
                  }`}
                >
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Findings ({findings.length})
                </button>
                <button
                  onClick={() => setActiveTab('compliance')}
                  className={`py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'compliance'
                      ? 'border-emerald-500 text-emerald-400'
                      : 'border-transparent text-slate-400 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Compliance ({complianceReports.length})
                </button>
                <button
                  onClick={() => setActiveTab('suppressed')}
                  className={`py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'suppressed'
                      ? 'border-amber-500 text-amber-400'
                      : 'border-transparent text-slate-400 hover:text-white'
                  }`}
                  data-testid="tab-suppressed"
                >
                  <EyeOff className="w-4 h-4 inline mr-2" />
                  Suppressed ({findings.filter((f) => f.status === 'suppressed').length})
                </button>
              </div>
            </div>

            {/* Findings Tab */}
            {activeTab === 'findings' && (
              <Card className="bg-slate-800 border-slate-700">
                {/* Filters */}
                <div className="p-4 border-b border-slate-700">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search findings..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        data-testid="findings-search"
                      />
                    </div>
                    
                    {/* Severity Filter */}
                    <select
                      value={filters.severity}
                      onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-300 focus:ring-2 focus:ring-emerald-500"
                      data-testid="severity-filter"
                    >
                      <option value="">All Severities</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                      <option value="info">Info</option>
                    </select>
                    
                    {/* Category Filter */}
                    <select
                      value={filters.category}
                      onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-300 focus:ring-2 focus:ring-emerald-500"
                      data-testid="category-filter"
                    >
                      <option value="">All Categories</option>
                      {getUniqueValues('category').map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    
                    {/* Status Filter */}
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-300 focus:ring-2 focus:ring-emerald-500"
                      data-testid="status-filter"
                    >
                      <option value="">All Status</option>
                      <option value="fail">Fail</option>
                      <option value="pass">Pass</option>
                    </select>
                    
                    {/* Region Filter */}
                    <select
                      value={filters.region}
                      onChange={(e) => setFilters(prev => ({ ...prev, region: e.target.value }))}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-300 focus:ring-2 focus:ring-emerald-500"
                      data-testid="region-filter"
                    >
                      <option value="">All Regions</option>
                      {getUniqueValues('region').map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                    
                    {/* Clear Filters */}
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-slate-400 hover:text-white"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    )}
                    
                    <span className="text-sm text-slate-500 ml-auto">
                      Showing {filteredFindings.length} of {findings.length}
                    </span>
                  </div>
                </div>

                {/* Bulk Action Bar (Feb 2026) */}
                {selectedIds.size > 0 && (
                  <div
                    className="sticky top-16 z-20 mx-4 mb-2 px-4 py-3 bg-indigo-900/70 backdrop-blur border border-indigo-500/40 rounded-lg flex items-center justify-between"
                    data-testid="bulk-action-bar"
                  >
                    <div className="text-sm text-indigo-100">
                      <span className="font-semibold" data-testid="bulk-selected-count">{selectedIds.size}</span> finding{selectedIds.size === 1 ? '' : 's'} selected
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setBulkMode('suppress');
                          setSuppressReason('');
                          setSuppressExpires('');
                          setSuppressScope('resource');
                        }}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="bulk-suppress-btn"
                      >
                        <EyeOff className="w-4 h-4 mr-2" />
                        Bulk Suppress
                      </Button>
                      <Button
                        size="sm"
                        onClick={bulkSendToJira}
                        disabled={bulkBusy}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="bulk-jira-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {bulkBusy ? 'Sending…' : 'Bulk Send to Jira'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={clearSelection}
                        className="text-indigo-200 hover:text-white"
                        data-testid="bulk-clear-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  </div>
                )}

                {/* Findings Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700 bg-slate-800/50">
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase w-10">
                          <input
                            type="checkbox"
                            checked={filteredFindings.length > 0 && filteredFindings.every((f) => selectedIds.has(f.id))}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                const all = new Set(selectedIds);
                                filteredFindings.forEach((f) => f.id && all.add(f.id));
                                setSelectedIds(all);
                              } else {
                                const all = new Set(selectedIds);
                                filteredFindings.forEach((f) => f.id && all.delete(f.id));
                                setSelectedIds(all);
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-500 bg-slate-700"
                            data-testid="bulk-select-all"
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase w-12"></th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Finding</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Source</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Resource</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Severity</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Region</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFindings.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-12 text-center">
                            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                            <p className="text-slate-400">No findings match the current filters</p>
                          </td>
                        </tr>
                      ) : (
                        filteredFindings.map((finding, idx) => (
                          <React.Fragment key={finding.id || idx}>
                            <tr 
                              className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
                              onClick={() => setExpandedFinding(expandedFinding === idx ? null : idx)}
                              data-testid={`finding-row-${idx}`}
                            >
                              <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(finding.id)}
                                  onChange={() => finding.id && toggleSelected(finding.id)}
                                  className="w-4 h-4 rounded border-slate-500 bg-slate-700"
                                  data-testid={`bulk-select-${idx}`}
                                />
                              </td>
                              <td className="py-3 px-4">
                                <button className="p-1 hover:bg-slate-600 rounded">
                                  {expandedFinding === idx ? (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                  )}
                                </button>
                              </td>
                              <td className="py-3 px-4 text-sm text-white max-w-md">
                                <div className="truncate">{finding.title}</div>
                                <div className="text-xs text-slate-500 truncate">{finding.check_id}</div>
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={
                                  finding.source === 'security_hub' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' :
                                  finding.source === 'guardduty' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
                                  finding.source === 'defender_for_cloud' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                                  finding.source === 'gcp_scc' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                                  'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                                }>
                                  {finding.source === 'security_hub' ? 'Security Hub' :
                                   finding.source === 'guardduty' ? 'GuardDuty' :
                                   finding.source === 'defender_for_cloud' ? 'Defender' :
                                   finding.source === 'gcp_scc' ? 'GCP SCC' : 'Amidala'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-xs text-slate-400 font-mono max-w-xs truncate" title={finding.resource?.resource_arn || finding.resource?.resource_id}>
                                {finding.resource?.resource_arn || finding.resource?.resource_id || finding.resource_id || '—'}
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={`
                                  ${finding.severity === 'critical' ? 'bg-pink-500/20 text-pink-400 border-pink-500/30' : ''}
                                  ${finding.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : ''}
                                  ${finding.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : ''}
                                  ${finding.severity === 'low' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' : ''}
                                  ${finding.severity === 'info' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : ''}
                                `}>
                                  {finding.severity}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                {(() => {
                                  const s = (finding.status || '').toLowerCase();
                                  // Amidala emits status='open' for failing checks.
                                  // Treat open/fail → FAIL; resolved/pass → PASS; suppressed → SUPPRESSED.
                                  const label = s === 'pass' || s === 'resolved'
                                    ? 'PASS'
                                    : s === 'suppressed'
                                    ? 'SUPPRESSED'
                                    : 'FAIL';
                                  const cls = label === 'PASS'
                                    ? 'text-emerald-400 border-emerald-400/30'
                                    : label === 'SUPPRESSED'
                                    ? 'text-slate-400 border-slate-500/30'
                                    : 'text-red-400 border-red-400/30';
                                  return (
                                    <Badge variant="outline" className={cls} data-testid="finding-status-badge">
                                      {label}
                                    </Badge>
                                  );
                                })()}
                              </td>
                              <td className="py-3 px-4 text-xs text-slate-400">
                                {finding.resource?.region || finding.region || 'global'}
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <span>{categoryIcons[finding.category?.toLowerCase()] || '📦'}</span>
                                  {finding.category}
                                </span>
                              </td>
                            </tr>
                            
                            {/* Expanded Row */}
                            {expandedFinding === idx && (
                              <tr className="bg-slate-700/20">
                                <td colSpan={9} className="p-6">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Resource Details */}
                                    <div className="space-y-3">
                                      <h4 className="text-xs font-medium text-slate-400 uppercase flex items-center gap-2">
                                        <Server className="w-4 h-4" />
                                        Resource Details
                                      </h4>
                                      <div className="space-y-2">
                                        <div>
                                          <p className="text-xs text-slate-500">Resource ID</p>
                                          <p className="text-sm text-white font-mono break-all">
                                            {finding.resource?.resource_id || finding.resource_id || '—'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-xs text-slate-500">Resource Type</p>
                                          <p className="text-sm text-white">
                                            {finding.resource?.resource_type || finding.resource_type || finding.service || finding.category || '—'}
                                          </p>
                                        </div>
                                        {(finding.resource?.resource_arn) && (
                                          <div>
                                            <p className="text-xs text-slate-500">ARN</p>
                                            <p className="text-xs text-slate-300 font-mono break-all">{finding.resource.resource_arn}</p>
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                          <MapPin className="w-4 h-4 text-slate-500" />
                                          <span className="text-sm text-slate-300">{finding.resource?.region || finding.region || 'global'}</span>
                                        </div>
                                        {(finding.resource?.account_id) && (
                                          <div className="text-xs text-slate-500">
                                            Account: <span className="text-slate-300">{finding.resource.account_id}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Description */}
                                    <div className="space-y-3">
                                      <h4 className="text-xs font-medium text-slate-400 uppercase flex items-center gap-2">
                                        <Info className="w-4 h-4" />
                                        Description
                                      </h4>
                                      <p className="text-sm text-slate-300">
                                        {finding.description || 
                                          'This security finding indicates a potential vulnerability or misconfiguration that should be addressed to improve your cloud security posture.'
                                        }
                                      </p>
                                      {finding.compliance_frameworks && finding.compliance_frameworks.length > 0 && (
                                        <div className="mt-3">
                                          <p className="text-xs text-slate-500 mb-1">Compliance Frameworks</p>
                                          <div className="flex flex-wrap gap-1">
                                            {finding.compliance_frameworks.map((fw, i) => (
                                              <Badge key={i} variant="outline" className="text-xs text-slate-400 border-slate-600">
                                                {frameworkShortNames[fw] || fw}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Remediation */}
                                    <div className="space-y-3">
                                      <h4 className="text-xs font-medium text-slate-400 uppercase flex items-center gap-2">
                                        <Wrench className="w-4 h-4" />
                                        Remediation
                                      </h4>
                                      <p className="text-sm text-emerald-400">
                                        {finding.remediation || 
                                          'Review and update the resource configuration to align with security best practices. Enable encryption, restrict access, or update IAM policies as needed.'
                                        }
                                      </p>
                                      <div className="flex gap-2 mt-4">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setDetailFinding(finding)}
                                          className="text-emerald-400 border-emerald-400/30 hover:bg-emerald-500/10"
                                          data-testid={`finding-view-details-${idx}`}
                                        >
                                          <FileText className="w-4 h-4 mr-2" />
                                          View Details
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setLinkFindingModal(finding)}
                                          className="text-indigo-300 border-indigo-500/40 hover:bg-indigo-500/10"
                                          data-testid={`finding-add-evidence-${idx}`}
                                        >
                                          <FileText className="w-4 h-4 mr-2" />
                                          Add to Evidence
                                        </Button>
                                        {isSTSFailed(finding) && (
                                          <Button
                                            size="sm"
                                            onClick={() => openSetupWizard(finding)}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            data-testid={`finding-open-setup-wizard-${idx}`}
                                          >
                                            <Rocket className="w-4 h-4 mr-2" />
                                            Open Setup Wizard
                                          </Button>
                                        )}
                                        {/* Send to Jira / View in Jira */}
                                        {(finding.jira?.issue_key || jiraLinks[finding.id]?.issue_key) ? (
                                          <>
                                            <a
                                              href={(finding.jira?.issue_url) || (jiraLinks[finding.id]?.issue_url)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                                              data-testid={`finding-jira-link-${idx}`}
                                            >
                                              <ExternalLink className="w-4 h-4" />
                                              {finding.jira?.issue_key || jiraLinks[finding.id]?.issue_key}
                                            </a>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => syncJira(finding)}
                                              disabled={jiraBusyId === finding.id}
                                              className="text-blue-300 border-blue-500/40 hover:bg-blue-500/10"
                                              data-testid={`finding-jira-sync-${idx}`}
                                            >
                                              <RefreshCw className={`w-4 h-4 mr-2 ${jiraBusyId === finding.id ? 'animate-spin' : ''}`} />
                                              Sync
                                            </Button>
                                          </>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => sendToJira(finding)}
                                            disabled={jiraBusyId === finding.id}
                                            className="text-blue-300 border-blue-500/40 hover:bg-blue-500/10"
                                            data-testid={`finding-send-jira-${idx}`}
                                          >
                                            <Send className={`w-4 h-4 mr-2 ${jiraBusyId === finding.id ? 'animate-pulse' : ''}`} />
                                            {jiraBusyId === finding.id ? 'Sending…' : 'Send to Jira'}
                                          </Button>
                                        )}
                                        {/* Suppress / Unsuppress */}
                                        {finding.status === 'suppressed' ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => unsuppressFinding(finding)}
                                            className="text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
                                            data-testid={`finding-unsuppress-${idx}`}
                                          >
                                            <Eye className="w-4 h-4 mr-2" />
                                            Unsuppress
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openSuppressModal(finding)}
                                            className="text-slate-300 border-slate-500/40 hover:bg-slate-700/40"
                                            data-testid={`finding-suppress-${idx}`}
                                          >
                                            <EyeOff className="w-4 h-4 mr-2" />
                                            Suppress
                                          </Button>
                                        )}
                                        {(() => {
                                          const url = buildConsoleUrl(finding);
                                          return url ? (
                                            <a
                                              href={url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              data-testid={`finding-console-${idx}`}
                                            >
                                              <Button size="sm" variant="outline" className="text-slate-400 border-slate-600 hover:bg-slate-700">
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                Console
                                              </Button>
                                            </a>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              disabled
                                              className="text-slate-500 border-slate-700 cursor-not-allowed"
                                              title="No deep link available for this resource"
                                            >
                                              <ExternalLink className="w-4 h-4 mr-2" />
                                              Console
                                            </Button>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Compliance Tab */}
            {activeTab === 'compliance' && (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  {complianceReports.length === 0 ? (
                    <div className="py-12 text-center">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                      <p className="text-slate-400">No compliance reports available</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {complianceReports.map((report) => (
                        <div key={report.id} className="p-4 bg-slate-700/50 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-white">
                                {frameworkShortNames[report.framework] || report.framework}
                              </h4>
                              <p className="text-xs text-slate-400">{report.framework}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-emerald-400">
                                {report.compliance_score?.toFixed(1) || 0}%
                              </p>
                              <p className="text-xs text-slate-500">Compliance Score</p>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                report.compliance_score >= 80 ? 'bg-emerald-500' :
                                report.compliance_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${report.compliance_score || 0}%` }}
                            />
                          </div>
                          
                          {/* Control Stats */}
                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <div className="text-center">
                              <p className="text-lg font-semibold text-emerald-400">{report.passed_controls || 0}</p>
                              <p className="text-xs text-slate-400">Passed</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-red-400">{report.failed_controls || 0}</p>
                              <p className="text-xs text-slate-400">Failed</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-slate-400">{report.total_controls || 0}</p>
                              <p className="text-xs text-slate-400">Total</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Suppressed Tab */}
            {activeTab === 'suppressed' && (
              <Card className="bg-slate-800 border-slate-700" data-testid="suppressed-tab-panel">
                <CardHeader>
                  <CardTitle className="text-lg text-amber-300 flex items-center gap-2">
                    <EyeOff className="w-5 h-5" />
                    Suppressed Findings
                  </CardTitle>
                  <p className="text-xs text-slate-400 mt-1">
                    Findings excluded from future scans via a business justification.
                    Reversible per row.
                  </p>
                </CardHeader>
                <CardContent>
                  {findings.filter((f) => f.status === 'suppressed').length === 0 ? (
                    <div className="text-center py-12 text-slate-500" data-testid="suppressed-empty">
                      <EyeOff className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                      <p>No suppressed findings for this scan yet.</p>
                      <p className="text-xs mt-2">Use the <span className="text-amber-400">Suppress</span> button on any finding to mute it after a business review.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-700">
                      {findings.filter((f) => f.status === 'suppressed').map((f, i) => (
                        <div key={f.id || i} className="py-3 flex items-start gap-4" data-testid={`suppressed-row-${i}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{f.check_name || f.id}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              <span className="text-slate-300">Reason:</span> {f.suppression?.reason || '—'}
                            </p>
                            <div className="flex gap-3 mt-1 text-[11px] text-slate-500">
                              {f.suppression?.expires_at ? (
                                <span>Expires: {new Date(f.suppression.expires_at).toLocaleDateString()}</span>
                              ) : (
                                <span>Permanent</span>
                              )}
                              {f.suppression?.scope && <span>Scope: {f.suppression.scope}</span>}
                              {f.resource?.resource_id && (
                                <span className="truncate max-w-[400px]">Resource: {f.resource.resource_id}</span>
                              )}
                            </div>
                          </div>
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 shrink-0">
                            {(f.severity || 'medium').toUpperCase()}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => unsuppressFinding(f)}
                            className="text-amber-300 border-amber-500/40 hover:bg-amber-500/10 shrink-0"
                            data-testid={`suppressed-unsuppress-${i}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Unsuppress
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Finding Details Modal */}
        {detailFinding && (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            data-testid="finding-details-modal"
            onClick={() => setDetailFinding(null)}
          >
            <div
              className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-5 border-b border-slate-700">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`
                      ${detailFinding.severity === 'critical' ? 'bg-pink-500/20 text-pink-400 border-pink-500/30' : ''}
                      ${detailFinding.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : ''}
                      ${detailFinding.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : ''}
                      ${detailFinding.severity === 'low' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' : ''}
                    `}>
                      {detailFinding.severity?.toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="text-slate-400 border-slate-600">
                      {detailFinding.source === 'security_hub' ? 'Security Hub'
                        : detailFinding.source === 'guardduty' ? 'GuardDuty'
                        : detailFinding.source === 'defender_for_cloud' ? 'Defender'
                        : detailFinding.source === 'gcp_scc' ? 'GCP SCC'
                        : 'Amidala'}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-bold text-white break-words">{detailFinding.title || detailFinding.check_name}</h3>
                  <p className="text-xs text-slate-400 font-mono mt-1">{detailFinding.check_id}</p>
                </div>
                <button
                  onClick={() => setDetailFinding(null)}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white flex-shrink-0"
                  data-testid="finding-details-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto space-y-5">
                <section>
                  <h4 className="text-xs uppercase text-slate-400 mb-2 flex items-center gap-2">
                    <Server className="w-4 h-4" /> Resource
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Resource ID</p>
                      <p className="text-white font-mono break-all">{detailFinding.resource?.resource_id || detailFinding.resource_id || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Resource Type</p>
                      <p className="text-white">{detailFinding.resource?.resource_type || detailFinding.resource_type || detailFinding.service || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Provider</p>
                      <p className="text-white uppercase">{detailFinding.provider || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Region</p>
                      {(() => {
                        const r = detailFinding.resource?.region || detailFinding.region || 'global';
                        const svc = (detailFinding.service || detailFinding.resource?.resource_type || '').toLowerCase();
                        const isGlobalSvc = r === 'global' || svc.startsWith('iam') || svc === 's3';
                        return (
                          <p className="text-white" title={isGlobalSvc ? 'Global AWS service — not tied to any single region.' : undefined}>
                            {r}
                            {isGlobalSvc && <span className="text-slate-500 text-xs ml-1">(global service)</span>}
                          </p>
                        );
                      })()}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Account</p>
                      <p className="text-white font-mono text-xs">{detailFinding.resource?.account_id || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Service</p>
                      <p className="text-white uppercase">{detailFinding.service || '—'}</p>
                    </div>
                    {detailFinding.resource?.resource_arn && (
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500">ARN</p>
                        <p className="text-slate-300 font-mono text-xs break-all">{detailFinding.resource.resource_arn}</p>
                      </div>
                    )}
                  </div>
                </section>

                <section>
                  <h4 className="text-xs uppercase text-slate-400 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" /> Description
                  </h4>
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{detailFinding.description || '—'}</p>
                  {detailFinding.risk && (
                    <>
                      <h4 className="text-xs uppercase text-slate-400 mt-4 mb-2">Risk</h4>
                      <p className="text-sm text-amber-300 whitespace-pre-wrap">{detailFinding.risk}</p>
                    </>
                  )}
                </section>

                <section>
                  <h4 className="text-xs uppercase text-slate-400 mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Remediation
                  </h4>
                  <p className="text-sm text-emerald-400 whitespace-pre-wrap">{detailFinding.remediation || '—'}</p>
                  {detailFinding.remediation_code && (
                    <pre className="mt-2 text-xs bg-slate-800 border border-slate-700 rounded p-3 overflow-x-auto text-slate-200">
                      {detailFinding.remediation_code}
                    </pre>
                  )}
                </section>

                {detailFinding.compliance_mappings?.length > 0 && (
                  <section>
                    <h4 className="text-xs uppercase text-slate-400 mb-2">Compliance Mappings</h4>
                    <div className="flex flex-wrap gap-2">
                      {detailFinding.compliance_mappings.map((m, i) => (
                        <Badge key={i} variant="outline" className="text-xs text-slate-300 border-slate-600">
                          {(m.framework || '').toString()} · {m.control_id}
                        </Badge>
                      ))}
                    </div>
                  </section>
                )}

                {(detailFinding.first_seen || detailFinding.last_seen) && (
                  <section className="text-xs text-slate-500 grid grid-cols-2 gap-3">
                    <div>First seen: <span className="text-slate-300">{detailFinding.first_seen ? new Date(detailFinding.first_seen).toLocaleString() : '—'}</span></div>
                    <div>Last seen:  <span className="text-slate-300">{detailFinding.last_seen ? new Date(detailFinding.last_seen).toLocaleString() : '—'}</span></div>
                  </section>
                )}
              </div>

              <div className="p-5 border-t border-slate-700 flex justify-between items-center">
                <p className="text-xs text-slate-500 font-mono truncate max-w-md">{detailFinding.id}</p>
                <div className="flex gap-2">
                  {isSTSFailed(detailFinding) && (
                    <Button
                      onClick={() => {
                        openSetupWizard(detailFinding);
                        setDetailFinding(null);
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="finding-details-open-setup-wizard"
                    >
                      <Rocket className="w-4 h-4 mr-2" /> Open Setup Wizard
                    </Button>
                  )}
                  {(() => {
                    const url = buildConsoleUrl(detailFinding);
                    return url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="finding-details-console-link">
                        <Button variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800">
                          <ExternalLink className="w-4 h-4 mr-2" /> Open in Console
                        </Button>
                      </a>
                    ) : null;
                  })()}
                  <Button
                    onClick={() => setDetailFinding(null)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="finding-details-close-btn"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Link Amidala Finding → GRC Control Modal */}
        {linkFindingModal && (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            data-testid="link-finding-modal"
          >
            <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Add Finding to Evidence</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    A GRC evidence record will be created with tag
                    <span className="ml-1 font-mono bg-slate-900 px-1.5 py-0.5 rounded">
                      Amidala Automated Finding
                    </span>
                    and linked to the selected control.
                  </p>
                </div>
                <button
                  onClick={() => setLinkFindingModal(null)}
                  className="text-slate-400 hover:text-white text-xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="text-xs text-slate-400 mb-1">Finding</div>
              <div className="mb-4 p-2 bg-slate-900 rounded font-mono text-xs text-slate-300 truncate">
                {linkFindingModal.title || linkFindingModal.check_id}
              </div>
              <div className="mb-4 p-3 bg-slate-900/60 rounded border border-slate-700">
                <div className="text-xs text-slate-400 mb-2">Auto-mapped Controls (SOC 2 / ISO 27001 / NIST 800-53)</div>
                <div className="space-y-1" data-testid="quick-evidence-controls-preview">
                  {(() => {
                    const cid = linkFindingModal.check_id || '';
                    // Client-side friendly mapping preview (kept in sync with backend FRIENDLY_CONTROL_MAPPINGS)
                    const MAP = {
                      aws_iam_root_access_key: [['SOC 2','CC6.1'],['ISO 27001','A.5.15'],['NIST 800-53','AC-2']],
                      aws_iam_mfa_root: [['SOC 2','CC6.1'],['ISO 27001','A.5.17'],['NIST 800-53','IA-2(1)']],
                      aws_s3_bucket_public_access: [['SOC 2','CC6.6'],['ISO 27001','A.8.24'],['NIST 800-53','AC-3']],
                      aws_s3_bucket_encryption: [['SOC 2','CC6.7'],['ISO 27001','A.8.24'],['NIST 800-53','SC-28']],
                      aws_cloudtrail_enabled: [['SOC 2','CC7.2'],['ISO 27001','A.8.15'],['NIST 800-53','AU-2']],
                      aws_guardduty_enabled: [['SOC 2','CC7.3'],['ISO 27001','A.8.16'],['NIST 800-53','SI-4']],
                      aws_kms_key_rotation: [['SOC 2','CC6.7'],['ISO 27001','A.8.24'],['NIST 800-53','SC-12']],
                    };
                    const controls = MAP[cid] || [['SOC 2','CC7.1'],['ISO 27001','A.8.8'],['NIST 800-53','RA-5']];
                    return controls.map(([fw, ctrl]) => (
                      <div key={`${fw}-${ctrl}`} className="flex items-center gap-2 text-xs">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-600/20 text-indigo-300 font-mono w-24">{fw}</span>
                        <span className="font-mono text-slate-200">{ctrl}</span>
                      </div>
                    ));
                  })()}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  No manual control selection needed. Evidence will be tagged with these friendly controls.
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setLinkFindingModal(null)}>
                  Cancel
                </Button>
                <Button
                  data-testid="link-finding-confirm-btn"
                  onClick={linkFindingToControl}
                  disabled={linkingControl}
                >
                  {linkingControl ? 'Adding…' : 'Add to Evidence'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Suppress Modal (single OR bulk when bulkMode === 'suppress') */}
        {(suppressModal || bulkMode === 'suppress') && (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            data-testid="suppress-modal"
            onClick={() => !suppressBusy && !bulkBusy && (suppressModal ? setSuppressModal(null) : setBulkMode(null))}
          >
            <div
              className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <EyeOff className="w-5 h-5 text-amber-400" />
                  {bulkMode === 'suppress' ? `Suppress ${selectedIds.size} finding(s)` : 'Suppress Finding'}
                </h2>
                <button
                  onClick={() => !suppressBusy && !bulkBusy && (suppressModal ? setSuppressModal(null) : setBulkMode(null))}
                  className="text-slate-400 hover:text-white"
                  data-testid="suppress-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {bulkMode === 'suppress' ? (
                  <div className="bg-indigo-900/40 border border-indigo-500/40 rounded p-3 text-xs text-indigo-100">
                    You&apos;re about to apply this same justification, scope, and expiry to <strong>{selectedIds.size}</strong> selected findings.
                  </div>
                ) : (
                  <div className="bg-slate-900 border border-slate-700 rounded p-3 text-xs text-slate-300">
                    <p className="font-medium text-white truncate">{suppressModal?.check_name || suppressModal?.id}</p>
                    <p className="text-slate-400 mt-1 truncate">
                      {suppressModal?.resource?.resource_id || suppressModal?.resource?.resource_arn || '—'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Scope
                  </label>
                  <select
                    value={suppressScope}
                    onChange={(e) => setSuppressScope(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                    data-testid="suppress-scope-select"
                  >
                    <option value="resource">This resource only (recommended)</option>
                    <option value="check">Every resource for this check (tenant-wide)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Business justification <span className="text-rose-400">*</span>
                  </label>
                  <textarea
                    value={suppressReason}
                    onChange={(e) => setSuppressReason(e.target.value)}
                    placeholder="e.g. Approved by security review board on 09/08/2026 — accepted risk pending Q4 refactor."
                    rows={4}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                    data-testid="suppress-reason-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Expires on <span className="text-slate-500">(optional — leave blank for permanent)</span>
                  </label>
                  <input
                    type="date"
                    value={suppressExpires}
                    onChange={(e) => setSuppressExpires(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                    data-testid="suppress-expires-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    On expiry the platform auto-reopens the finding — the next scheduled scan decides its fate.
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-slate-700 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => (suppressModal ? setSuppressModal(null) : setBulkMode(null))}
                  disabled={suppressBusy || bulkBusy}
                  className="text-slate-300 border-slate-600"
                  data-testid="suppress-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={bulkMode === 'suppress' ? bulkSubmitSuppress : submitSuppression}
                  disabled={suppressBusy || bulkBusy || (suppressReason || '').trim().length < 3}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="suppress-submit-btn"
                >
                  {(suppressBusy || bulkBusy) ? 'Suppressing…' : (bulkMode === 'suppress' ? `Suppress ${selectedIds.size} finding(s)` : 'Suppress finding')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmidalaScanDetail;
